import {
  type ButtonColors,
  buildButtonColorVarDeclarations,
  buildButtonHoverTextRules,
  buildSecondaryHoverBackgroundRule,
} from "./build-button-css-rules";
import { buildInputFieldRules } from "./build-input-field-css-rules";
import { hexToRgb, resolveDividerColor } from "./luminance";

export interface LoginCardBranding {
  cardBackground?: string | null;
  cardOpacity?: number | null;
  cardBlur?: number | null;
  cardCornerRadius?: number | null;
}

export interface LoginCardColors {
  headingText?: string | null;
  bodyText?: string | null;
  loginButtonDisabledBackground?: string | null;
  loginButtonDisabledText?: string | null;
}

export interface LoginTypography {
  /** Login-only heading font, already resolved through the font-fallback
   * helper. Pass only the login override — when unset, the general heading
   * rules keep applying. */
  headingFont?: string | null;
  headingFontWeight?: number | null;
  /** Login-only body font, already resolved through the font-fallback
   * helper. */
  bodyFont?: string | null;
  bodyFontWeight?: number | null;
}

export interface LoginButtons {
  primary?: { colors?: ButtonColors | null } | null;
  secondary?: { colors?: ButtonColors | null } | null;
}

export interface LoginInputFieldColors {
  background?: string | null;
  border?: string | null;
  text?: string | null;
  error?: string | null;
}

export interface LoginInputFields {
  colors?: LoginInputFieldColors | null;
}

/** Elements that count as "headings" on the login page. Mirrors the general
 * heading selectors (h1–h3, [data-ui="header"]) plus the gift-form section
 * labels, matching the coverage of the login headingText color override. */
function loginHeadingSelector(s: string): string {
  return ["h1", "h2", "h3", '[data-ui="header"]', '[data-ui="section-heading"]']
    .map((sel) => `${s}[data-ui="login-card"] ${sel}`)
    .join(", ");
}

export function buildLoginCardCssRules(
  s: string,
  branding: LoginCardBranding | null | undefined,
  colors?: LoginCardColors | null | undefined,
  typography?: LoginTypography | null | undefined,
  buttons?: LoginButtons | null | undefined,
  inputFields?: LoginInputFields | null | undefined,
): string[] {
  const rules: string[] = [];

  const rawBg = branding?.cardBackground;
  const customBackground =
    typeof rawBg === "string" && rawBg.length > 0 ? rawBg : undefined;

  const rawOpacity = branding?.cardOpacity;
  const opacity =
    typeof rawOpacity === "number" && !Number.isNaN(rawOpacity)
      ? Math.min(1, Math.max(0, rawOpacity))
      : undefined;

  const backgroundColor = resolveCardBackgroundColor(customBackground, opacity);
  if (backgroundColor !== undefined) {
    rules.push(
      `${s}[data-ui="login-card"] { background-color: ${backgroundColor}; }`,
    );
  }

  const rawBlur = branding?.cardBlur;
  if (typeof rawBlur === "number" && !Number.isNaN(rawBlur)) {
    const blur = Math.min(40, Math.max(0, rawBlur));
    if (blur > 0) {
      rules.push(
        `${s}[data-ui="login-card"] { backdrop-filter: blur(${blur}px); -webkit-backdrop-filter: blur(${blur}px); }`,
      );
    }
  }

  // Login card corner radius override. `!important` is required: the
  // global-card radius rule (same specificity, emitted later in the sheet)
  // and the design system's lazily-loaded `.radius-md` class would otherwise
  // tie and win on order.
  const rawCornerRadius = branding?.cardCornerRadius;
  if (typeof rawCornerRadius === "number" && !Number.isNaN(rawCornerRadius)) {
    const radius = Math.max(0, rawCornerRadius);
    rules.push(
      `${s}[data-ui="login-card"] { border-radius: ${radius}px !important; }`,
    );
  }

  // "Body text" applies to the subtitle (below the h1 inside the header,
  // tagged data-ui="login-subtitle") and the link buttons (gift, register,
  // return policy). Link buttons are RedoButton hierarchy="link_underline"
  // which reads a separate design-system token, so we override it directly.
  const bodyText = colors?.bodyText;
  if (typeof bodyText === "string" && bodyText.length > 0) {
    rules.push(
      `${s}[data-ui="login-card"] [data-ui="login-subtitle"] ` +
        `{ color: ${bodyText}; }`,
    );
    rules.push(
      `${s}[data-ui="login-card"] button[data-hierarchy="link_underline"] ` +
        `{ color: ${bodyText}; }`,
    );
  }

  // "Heading text" applies to the h1 title, section headings (e.g. the
  // "Buyer info" / "Your info" labels on the gift-return form), and the
  // labels above input fields. Input labels render as
  // <Text textColor="secondary"> → they read --redo-colors-text-text-secondary,
  // so overriding that variable scoped to the login card recolors them
  // without leaking to other elements that explicitly read their own tokens.
  const headingText = colors?.headingText;
  if (typeof headingText === "string" && headingText.length > 0) {
    rules.push(
      `${s}[data-ui="login-card"] h1, ` +
        `${s}[data-ui="login-card"] [data-ui="section-heading"] ` +
        `{ color: ${headingText}; }`,
    );
    rules.push(
      `${s}[data-ui="login-card"] ` +
        `{ --redo-colors-text-text-secondary: ${headingText}; }`,
    );
  }

  const loginButtonDisabledProps: string[] = [];
  const loginButtonDisabledBackground = colors?.loginButtonDisabledBackground;
  if (
    typeof loginButtonDisabledBackground === "string" &&
    loginButtonDisabledBackground.length > 0
  ) {
    loginButtonDisabledProps.push(
      `background-color: ${loginButtonDisabledBackground};`,
    );
  }
  const loginButtonDisabledText = colors?.loginButtonDisabledText;
  if (
    typeof loginButtonDisabledText === "string" &&
    loginButtonDisabledText.length > 0
  ) {
    loginButtonDisabledProps.push(`color: ${loginButtonDisabledText};`);
  }
  if (loginButtonDisabledProps.length > 0) {
    rules.push(
      `${s}[data-ui="login-card"] button[data-hierarchy="primary"]:disabled ` +
        `{ ${loginButtonDisabledProps.join(" ")} }`,
    );
  }

  // Login-only heading font/weight. `!important` mirrors the general heading
  // rules (build-typography-css-rules.ts); the login-scoped selector wins on
  // specificity, so the override beats the general one regardless of order.
  const headingFont = typography?.headingFont;
  if (typeof headingFont === "string" && headingFont.length > 0) {
    rules.push(
      `${loginHeadingSelector(s)} { font-family: ${headingFont} !important; }`,
    );
  }
  const headingFontWeight = typography?.headingFontWeight;
  if (
    typeof headingFontWeight === "number" &&
    !Number.isNaN(headingFontWeight)
  ) {
    rules.push(
      `${loginHeadingSelector(s)} { font-weight: ${headingFontWeight} !important; }`,
    );
  }

  // Login-only body font/weight. Set on the login page wrapper
  // ([data-ui="login"], which contains the card, the back-to-store link, and
  // the locale selector) so everything inherits it — the same mechanism the
  // general body font uses on the scope root, but on a closer ancestor so the
  // login value wins. Headings are unaffected: they carry their own
  // font-family from the heading rules above (or the general heading rule).
  const bodyFont = typography?.bodyFont;
  if (typeof bodyFont === "string" && bodyFont.length > 0) {
    rules.push(`${s}[data-ui="login"] { font-family: ${bodyFont}; }`);
  }
  const bodyFontWeight = typography?.bodyFontWeight;
  if (typeof bodyFontWeight === "number" && !Number.isNaN(bodyFontWeight)) {
    // `!important` mirrors the general body-weight rule: the design system's
    // `.weight-*` classes set font-weight directly on elements and would
    // otherwise win over an inherited value.
    rules.push(
      `${s}[data-ui="login"] { font-weight: ${bodyFontWeight} !important; }`,
    );
  }

  // Login-only button colors. Re-declaring the button color CSS variables on
  // the login card recolors descendant [data-hierarchy] buttons via variable
  // inheritance, with the same auto-derived hover/pressed behavior as the
  // general buttons customization.
  for (const hierarchy of ["primary", "secondary"] as const) {
    const buttonColors = buttons?.[hierarchy]?.colors;
    if (!buttonColors) {
      continue;
    }
    const declarations = buildButtonColorVarDeclarations(
      hierarchy,
      buttonColors,
    );
    if (declarations.length > 0) {
      rules.push(`${s}[data-ui="login-card"] { ${declarations.join(" ")} }`);
    }
    const loginScope = `${s}[data-ui="login-card"] `;
    rules.push(
      ...buildButtonHoverTextRules(loginScope, hierarchy, buttonColors),
    );
    if (hierarchy === "secondary") {
      rules.push(
        ...buildSecondaryHoverBackgroundRule(loginScope, buttonColors),
      );
    }
  }

  // Login-only input field colors. Reuse the general input-field rule builder
  // but prefixed with the login-card scope so the override only recolors the
  // email/order-number/gift inputs inside the login card. The error CSS vars
  // are declared on the login card itself (not :root) to stay scoped.
  if (inputFields?.colors) {
    const loginCardScope = `${s}[data-ui="login-card"]`;
    rules.push(
      ...buildInputFieldRules(
        `${loginCardScope} `,
        { colors: inputFields.colors },
        { errorVarScope: loginCardScope },
      ),
    );
  }

  // Auto-pick a divider color that complements the card background. The
  // <Divider/> footer separator reads `--redo-colors-border-border-primary`,
  // which defaults to a near-black hairline that disappears on dark cards. We
  // only override when the merchant has set a custom card background — the
  // existing default is correct for the stock white card.
  const dividerColor = resolveDividerColor(customBackground);
  if (dividerColor !== undefined) {
    rules.push(
      `${s}[data-ui="login-card"] ` +
        `{ --redo-colors-border-border-primary: ${dividerColor}; }`,
    );
  }

  return rules;
}

function resolveCardBackgroundColor(
  customBackground: string | undefined,
  opacity: number | undefined,
): string | undefined {
  if (customBackground === undefined) {
    if (opacity === undefined || opacity >= 1) {
      return undefined;
    }
    return `rgba(255, 255, 255, ${opacity})`;
  }

  if (opacity === undefined || opacity >= 1) {
    return customBackground;
  }

  const rgb = hexToRgb(customBackground);
  if (rgb) {
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
  }
  return customBackground;
}
